import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../componentes/Plantilla/Plantilla';
import ContactSection from '../../componentes/Secciones/Contacto/SeccionContacto';
import VideoPlayer from '../../componentes/UI/VideoPlayer';

import Breadcrumbs from '../../componentes/UI/Breadcrumbs';
import styles from './ServicioDetalle.module.css';

const BASE = '/imagenes/proyectos';

const useReveal = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.1 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
};

const equipos = [
  { nombre: 'Lavadoras extractoras', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="28" height="28"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="12" cy="14" r="4"/><path d="M7 7h10"/></svg> },
  { nombre: 'Secadoras tumbler', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="28" height="28"><path d="M12 22a10 10 0 100-20 10 10 0 000 20z"/><circle cx="12" cy="12" r="6"/><path d="M12 6v6l4 2"/></svg> },
  { nombre: 'Calandras de secado', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="28" height="28"><rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="2"/></svg> },
  { nombre: 'Planchadoras industriales', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="28" height="28"><path d="M3 10h18l-2 10H5L3 10z"/><path d="M12 10V4"/></svg> },
];

const beneficios = [
  { valor: '↑ Vida útil', desc: 'Extensión de la vida operativa de su equipo mediante mantenimiento continuo', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg> },
  { valor: '↓ Paradas', desc: 'Reducción de tiempos de inactividad no programados en su planta', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg> },
  { valor: '↓ Costos', desc: 'Prevención de reparaciones mayores y paros costosos de producción', icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg> },
];

const pasos = [
  { num: '01', titulo: 'Inspección inicial', sub: 'Diagnóstico general', desc: 'Verificación del estado actual de los componentes mecánicos y eléctricos del equipo.' },
  { num: '02', titulo: 'Limpieza profunda', sub: 'Desazolve y lubricación', desc: 'Retiro de excesos de pelusa, polvo y lubricación de rodamientos clave.' },
  { num: '03', titulo: 'Calibración', sub: 'Ajuste térmico', desc: 'Calibración de termostatos, válvulas y sensores para eficiencia óptima.' },
  { num: '04', titulo: 'Pruebas de funcionamiento', sub: 'Validación de ciclos', desc: 'Pruebas finales en vacío y con carga para asegurar su perfecta operatividad.' },
];

const fotos = [
  `${BASE}/lavanderia_lavadora_1_15.webp`,
  `/imagenes/servicios/lavanderia-img-1.webp`,
  `${BASE}/lavanderia_lavadora_3_21.webp`,
  `/imagenes/servicios/lavanderia-img-2.webp`,
  `${BASE}/lavanderia_lavadora_5_23.webp`,
  `/imagenes/servicios/lavanderia-img-3.webp`,
  `/imagenes/servicios/lavanderia-img-4.webp`,
  `${BASE}/lavanderia_lavadora_6_25.webp`,
];

const MantenimientoLavanderia = () => {
  const [heroRef, heroVis] = useReveal();
  const [objRef, objVis] = useReveal();
  const [alcRef, alcVis] = useReveal();
  const [procRef, procVis] = useReveal();
  const [vidRef, vidVis] = useReveal();
  const [galRef, galVis] = useReveal();

  const [lightboxSrc, setLightboxSrc] = useState(null);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') setLightboxSrc(null); };
    if (lightboxSrc) {
      window.addEventListener('keydown', handleKey);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [lightboxSrc]);

  return (
    <Layout>

      {/* ── HERO ── */}
      <section
        ref={heroRef}
        className={styles.hero}
        style={{ backgroundImage: `url(/imagenes/proyectos/lavanderia_lavadoras_dls_28_touch_ii_e_20.webp)` }}
      >
        <div className={styles.heroOverlay} />
        <div className={`container ${styles.heroContent}`}>
          <Breadcrumbs 
            paths={[
              { label: 'Inicio', to: '/' },
              { label: 'Servicios', to: '/servicios' },
              { label: 'Mantenimiento de Equipo' }
            ]}
          />
          <h1 className={`${styles.heroTitle} ${heroVis ? styles.titleVis : ''}`}>
            Mantenimiento de<br/><span className={styles.acento}>Lavandería Industrial</span>
          </h1>
          <div className={`${styles.heroLine} ${heroVis ? styles.lineVis : ''}`} />
          <p className={`${styles.heroSub} ${heroVis ? styles.subVis : ''}`}>
            Mantenimiento preventivo y correctivo para maximizar la vida útil de su equipo.
          </p>
          <div className={`${styles.heroCtas} ${heroVis ? styles.ctasVis : ''}`}>
            <a href="#contact" className="btn btn-primary">Solicitar cotización</a>
            <Link to="/servicios" className={styles.backLink}>← Todos los servicios</Link>
          </div>
        </div>
      </section>

      {/* ── OBJETIVO + FOTO CAMPO ── */}
      <section ref={objRef} className={styles.objetivoSection}>
        <div className="container">
          <div className={`${styles.objetivoGrid} ${objVis ? styles.gridVis : ''}`}>
            <div className={styles.objetivoTexto}>
              <h2 className={styles.secTitle}>
                Prevención inteligente<br/>
                <span className={styles.acento}>garantizada</span>
              </h2>
              <div className={styles.secLine} />
              <p className={styles.objetivoDesc}>
                Ofrecemos planes de mantenimiento preventivo y correctivo para toda clase de equipo de lavandería industrial. Nuestros técnicos certificados garantizan la operación continua de su planta y reducen los tiempos de paro no programado.
              </p>
              <div className={styles.specsBar}>
                <div className={styles.specItem}>
                  <span className={styles.specValor}>100%</span>
                  <span className={styles.specLabel}>Operatividad</span>
                </div>
                <div className={styles.specDivider} />
                <div className={styles.specItem}>
                  <span className={styles.specValor}>24/7</span>
                  <span className={styles.specLabel}>Soporte</span>
                </div>
              </div>
            </div>
            <div className={styles.objetivoFotoCol}>
              <div className={styles.objetivoFotoWrapSingle}>
                <img src={`/imagenes/proyectos/lavanderia_lavadora_7_26.webp`} alt="Mantenimiento de Equipo" className={styles.objetivoFoto} loading="lazy" />
              </div>
              
              <div className={styles.singleImageCaption}>
                <span className={styles.comparadorCaptionLabel}>GARANTÍA DE OPERACIÓN</span>
                <p className={styles.comparadorCaptionTexto}>Mantenimiento continuo para asegurar que su equipo opere al 100% de su capacidad nominal, previniendo fallas y retrasos.</p>
              </div>
            </div>
          </div>

          {/* Beneficios en fila debajo del grid */}
          <div className={styles.beneficiosRow}>
            {beneficios.map((b, i) => (
              <div
                key={i}
                className={`${styles.beneficioCard} ${objVis ? styles.cardVis : ''}`}
                style={{ transitionDelay: `${400 + i * 120}ms` }}
              >
                <div className={styles.beneficioIcon}>{b.icon}</div>
                <div>
                  <span className={styles.beneficioValor}>{b.valor}</span>
                  <p className={styles.beneficioDesc}>{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ALCANCE ── */}
      <section ref={alcRef} className={styles.alcanceSection}>
        <div className="container">
          <div className={`${styles.alcanceHeader} ${alcVis ? styles.headerVis : ''}`}>
            <h2 className={styles.secTitleDark}>Equipos que <span className={styles.acento}>intervenimos</span></h2>
            <div className={styles.secLine} />
          </div>
          <div className={styles.equiposGrid}>
            {equipos.map((e, i) => (
              <div
                key={i}
                className={`${styles.equipoCard} ${alcVis ? styles.equipoVis : ''}`}
                style={{ transitionDelay: `${i * 70}ms` }}
              >
                <div className={styles.equipoIcon}>{e.icon}</div>
                <span className={styles.equipoNombre}>{e.nombre}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PROCESO ── */}
      <section ref={procRef} className={styles.procesoSection}>
        <div className="container">
          <div className={`${styles.procesoHeader} ${procVis ? styles.headerVis : ''}`}>
            <h2 className={styles.secTitle}>Cómo <span className={styles.acento}>trabajamos</span></h2>
            <div className={styles.secLine} />
          </div>

          {/* Pasos + foto lateral */}
          <div className={styles.procesoLayout}>
            <div className={styles.pasosGrid}>
              {pasos.map((p, i) => (
                <div
                  key={i}
                  className={`${styles.pasoCard} ${procVis ? styles.pasoVis : ''}`}
                  style={{ transitionDelay: `${i * 100}ms` }}
                >
                  <div className={styles.pasoNum}>{p.num}</div>
                  <div className={styles.pasoContenido}>
                    <span className={styles.pasoSub}>{p.sub}</span>
                    <h3 className={styles.pasoTitulo}>{p.titulo}</h3>
                    <p className={styles.pasoDesc}>{p.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className={`${styles.procesoFotoCol} ${procVis ? styles.procesoFotoVis : ''}`}>
              <div className={styles.procesoFotoWrap}>
                <VideoPlayer 
                  src="/videos/servicios/lavanderia-vid-1.mp4" 
                  wrapperStyle={{ height: '100%' }}
                  videoStyle={{ height: '100%', objectFit: 'cover' }}
                />
              </div>
              <div className={`${styles.tiempoNote} ${procVis ? styles.noteVis : ''}`}>
                <div className={styles.noteIcon}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                    <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
                  </svg>
                </div>
                <p><strong>Duración estimada:</strong> varía según la cantidad de equipos a intervenir, su estado actual y el alcance del mantenimiento (preventivo o correctivo).</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── VIDEO PRINCIPAL ── */}
      <section ref={vidRef} className={styles.videoSection}>
        <div className="container">
          <div className={`${styles.videoHeader} ${vidVis ? styles.headerVis : ''}`} style={{ textAlign: 'center' }}>
            <h2 className={styles.secTitle}>
              <span className={styles.acento}>VIDEOS</span>
            </h2>
            <div className={styles.secLine} />
          </div>
          <div className={styles.videosLayout}>
            {/* Fila superior: dos videos verticales */}
            <div className={`${styles.videoCol2} ${vidVis ? styles.videoGridVis : ''}`}>
              <VideoPlayer src="/videos/servicios/lavanderia-vid-2.mp4" />
              <VideoPlayer src="/videos/servicios/lavanderia-vid-3.mp4" />
            </div>
            {/* Fila inferior: video horizontal */}
            <div className={`${styles.videoCol1} ${vidVis ? styles.videoGridVis : ''}`}>
              <VideoPlayer src="/videos/servicios/lavanderia-vid-4.mp4" />
            </div>
          </div>
        </div>
      </section>

      {/* ── GALERÍA ── */}
      <section ref={galRef} className={styles.galeriaSection}>
        <div className="container">
          <div className={`${styles.galeriaHeader} ${galVis ? styles.headerVis : ''}`} style={{ textAlign: 'center' }}>
            <h2 className={styles.secTitle}>
              <span className={styles.acento}>FOTOS</span>
            </h2>
            <div className={styles.secLine} />
          </div>
          <div className={styles.galeriaGrid}>
            {fotos.map((src, i) => (
              <div
                key={i}
                className={`${styles.fotoWrap} ${galVis ? styles.fotoVis : ''}`}
                style={{ transitionDelay: `${i * 80}ms` }}
                onClick={() => setLightboxSrc(src)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && setLightboxSrc(src)}
                aria-label={`Ver foto ${i + 1} en grande`}
              >
                <img src={src} alt={`Limpieza tubería industrial ${i + 1}`} className={styles.foto} loading="lazy" />
                <div className={styles.fotoOverlayHover}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="28" height="28">
                    <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35M11 8v6M8 11h6"/>
                  </svg>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className={styles.ctaSection}>
        <div className="container">
          <div className={styles.ctaCard}>
            <div className={styles.ctaTexto}>
              <h2 className={styles.ctaTitulo}>¿Listo para optimizar sus equipos?</h2>
              <p className={styles.ctaDesc}>Un especialista técnico evaluará sus equipos y le propondrá una póliza de mantenimiento adaptada a sus necesidades.</p>
            </div>
            <div className={styles.ctaBtns}>
              <a href="#contact" className="btn btn-primary">Solicitar evaluación gratuita</a>
              <Link to="/servicios" className="btn btn-secondary">← Ver todos los servicios</Link>
            </div>
          </div>
        </div>
      </section>

      <ContactSection />

      {/* ── LIGHTBOX ── */}
      {lightboxSrc && (
        <div className={styles.lightboxOverlay} onClick={() => setLightboxSrc(null)}>
          <button
            className={styles.lightboxClose}
            onClick={() => setLightboxSrc(null)}
            aria-label="Cerrar"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="18" height="18">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
          <img
            src={lightboxSrc}
            alt="Vista ampliada"
            className={styles.lightboxImg}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

    </Layout>
  );
};

export default MantenimientoLavanderia;
